// One owner for the catalog, the session and every notification.
//
// The old admin had the catalog in one component, three independent Snackbar
// systems that stacked on top of each other at the same anchor, a write-only
// `error` string, and four components that reported failure their own way.
// Everything below the shell now talks to this.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import * as api from './api';
import { sanitize } from './schema';

const AdminContext = createContext(null);

// An upload outlives the panel that started it: the tabs are conditionally
// rendered, so switching to Subtitles unmounts the uploader while its bytes are
// still going to api.video. Nothing cancels -- the running promise is a plain
// closure -- so the file lands and the record gets patched either way. What was
// lost was every sign that it was happening: coming back to Media showed a
// clean panel, which reads as "it died" and invites a second upload of the same
// file. Two api.video assets, two patches racing for the record, one orphan
// left behind billing storage.
//
// So the progress lives here instead, one level above anything that unmounts.
export const uploadKey = (identifier, kind) => `${identifier}:${kind}`;

// 'uploading' and 'registering' are in flight; 'done' and 'error' are the
// outcome, kept so returning to the tab shows what happened rather than the
// same clean slate.
export const UPLOAD_ACTIVE = new Set(['uploading', 'registering']);

export function isUploadActive(entry) {
  return !!entry && UPLOAD_ACTIVE.has(entry.state);
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used inside <AdminProvider>');
  return ctx;
}

export function AdminProvider({ children }) {
  const [videos, setVideos] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!api.getPassword());
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // key -> { identifier, kind, filename, progress, state, message }
  const [uploads, setUploads] = useState({});

  // The detail pane publishes whether its edit buffer has unsaved changes, so
  // that selecting another video asks first instead of discarding them.
  const [dirty, setDirty] = useState(false);
  const [pendingSelect, setPendingSelect] = useState(null);

  const select = useCallback(
    (id, { force = false } = {}) => {
      setSelectedId((current) => {
        if (!force && dirty && id !== current) {
          setPendingSelect({ id });
          return current;
        }
        return id;
      });
    },
    [dirty],
  );

  const confirmPendingSelect = useCallback(() => {
    setPendingSelect((pending) => {
      if (pending) {
        setDirty(false);
        setSelectedId(pending.id);
      }
      return null;
    });
  }, []);

  const cancelPendingSelect = useCallback(() => setPendingSelect(null), []);

  const toast = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const dismissToast = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Every path into `videos` goes through here, so a record can never carry a
  // password or a control key into the UI -- which is what used to render the
  // admin password as an editable field on the card.
  const applyRecord = useCallback((identifier, record) => {
    setVideos((prev) => ({ ...prev, [identifier]: sanitize(record) }));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setVideos(await api.getAll());
    } catch (error) {
      toast(api.describeError(error, 'The catalog could not be loaded.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Cheaper than refetching the whole catalog, which is what both pipeline
  // managers used to do to update a single record.
  const refreshOne = useCallback(
    async (identifier) => {
      try {
        applyRecord(identifier, await api.getVideo(identifier));
      } catch (error) {
        toast('The change was saved, but the view could not refresh.', 'warning');
      }
    },
    [applyRecord, toast],
  );

  const patch = useCallback(
    async (identifier, fields) => {
      const record = await api.patchVideo(identifier, fields);
      applyRecord(identifier, record);
      return record;
    },
    [applyRecord],
  );

  const deleteFields = useCallback(
    async (identifier, keys) => {
      const record = await api.deleteFields(identifier, keys);
      applyRecord(identifier, record);
      return record;
    },
    [applyRecord],
  );

  const remove = useCallback(
    async (identifier) => {
      await api.deleteVideo(identifier);
      setVideos((prev) => {
        const next = { ...prev };
        delete next[identifier];
        return next;
      });
      setSelectedId((current) => (current === identifier ? null : current));
    },
    [],
  );

  const login = useCallback(
    async (password) => {
      if (!password) {
        toast('Password cannot be empty.', 'warning');
        return false;
      }
      api.setPassword(password);
      setIsAuthenticated(true);
      return true;
    },
    [toast],
  );

  const beginUpload = useCallback((key, info) => {
    setUploads((prev) => ({ ...prev, [key]: { progress: 0, state: 'uploading', ...info } }));
  }, []);

  // Only patches an entry that is still there, so a cleared slot cannot be
  // resurrected by a late progress event from the upload that owned it.
  const updateUpload = useCallback((key, patch) => {
    setUploads((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
  }, []);

  const clearUpload = useCallback((key) => {
    setUploads((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const activeUploads = useMemo(
    () => Object.values(uploads).filter(isUploadActive),
    [uploads],
  );

  const logout = useCallback(() => {
    api.clearPassword();
    setIsAuthenticated(false);
    setVideos({});
    setSelectedId(null);
    toast('Logged out.', 'info');
  }, [toast]);

  // Derived once per catalog change, so the Overview form can offer what is
  // already in use instead of leaving both fields as free text -- which is how
  // near-duplicate categories accumulate.
  const knownCategories = useMemo(() => {
    const set = new Set();
    Object.values(videos).forEach((v) => v?.category && set.add(v.category));
    return [...set].sort();
  }, [videos]);

  const knownProducts = useMemo(() => {
    const set = new Set();
    Object.values(videos).forEach((v) => {
      const raw = v?.products;
      const list = Array.isArray(raw) ? raw : String(raw || '').split(',');
      list.forEach((p) => {
        const name = String(p).trim();
        if (name) set.add(name);
      });
    });
    return [...set].sort();
  }, [videos]);

  const value = useMemo(
    () => ({
      videos,
      selectedId,
      select,
      dirty,
      setDirty,
      pendingSelect,
      confirmPendingSelect,
      cancelPendingSelect,
      loading,
      isAuthenticated,
      login,
      logout,
      refresh,
      refreshOne,
      patch,
      deleteFields,
      remove,
      applyRecord,
      setVideos,
      toast,
      knownCategories,
      knownProducts,
      notification,
      dismissToast,
      uploads,
      activeUploads,
      beginUpload,
      updateUpload,
      clearUpload,
    }),
    [
      videos,
      selectedId,
      select,
      dirty,
      pendingSelect,
      confirmPendingSelect,
      cancelPendingSelect,
      loading,
      isAuthenticated,
      login,
      logout,
      refresh,
      refreshOne,
      patch,
      deleteFields,
      remove,
      applyRecord,
      toast,
      knownCategories,
      knownProducts,
      notification,
      dismissToast,
      uploads,
      activeUploads,
      beginUpload,
      updateUpload,
      clearUpload,
    ],
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
